import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Calendar, ClipboardList, TrendingUp, BookOpen, DollarSign, MessageSquare, User, LogOut, KeyRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ProfileForm from '../components/ProfileForm';
import GoalsSection from '../components/GoalsSection';
import FileUpload from '../components/FileUpload';
import ProfileAvatar from '../components/ProfileAvatar';
import TeacherStudentsSection from '../components/TeacherStudentsSection';
import NewsSection from '../components/NewsSection';
import KudosSection from '../components/KudosSection';
import BirthdaySection from '../components/BirthdaySection';
import NotificationBell from '../components/NotificationBell';
import useBirthdayGreeting from '../hooks/useBirthdayGreeting';
import FeedbackSection from '../components/FeedbackSection';
import TeacherPayslipsSection from '../components/TeacherPayslipsSection';
import PurchaseItemsSection from '../components/PurchaseItemsSection';
import CalendarEventsSection from '../components/CalendarEventsSection';

const API = process.env.REACT_APP_BACKEND_URL;

const TeacherDashboard = () => {
  const { user, logout } = useAuth();
  useBirthdayGreeting(user);
  const navigate = useNavigate();
  const [leaves, setLeaves] = useState([]);
  const [studentLeaves, setStudentLeaves] = useState([]);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [performances, setPerformances] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [claims, setClaims] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [attendanceToday, setAttendanceToday] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [attendanceYearMonth, setAttendanceYearMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Leave form
  const [leaveForm, setLeaveForm] = useState({
    leave_type: 'sick_leave',
    start_date: '',
    end_date: '',
    reason: '',
  });

  // Training form
  const [trainingForm, setTrainingForm] = useState({
    title: '',
    description: '',
    date: '',
    duration: '',
    certificate_url: '',
  });

  // Claim form
  const [claimForm, setClaimForm] = useState({
    claim_type: 'travel',
    transaction_date: '',
    amount: '',
    description: '',
    receipt_url: '',
  });

  // Feedback form
  const [feedbackForm, setFeedbackForm] = useState({
    subject: '',
    message: '',
  });

  // Forms published by admin
  const [forms, setForms] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/api/leaves`, { withCredentials: true }),
        axios.get(`${API}/api/student-leaves`, { withCredentials: true }),
        axios.get(`${API}/api/leaves/approved?year=${new Date().getFullYear()}`, { withCredentials: true }),
        axios.get(`${API}/api/performances`, { withCredentials: true }),
        axios.get(`${API}/api/trainings`, { withCredentials: true }),
        axios.get(`${API}/api/claims`, { withCredentials: true }),
        axios.get(`${API}/api/feedback`, { withCredentials: true }),
        axios.get(`${API}/api/leave-balance`, { withCredentials: true }),
        axios.get(`${API}/api/forms`, { withCredentials: true }),
      ]);
      const [leavesRes, studentLeavesRes, approvedLeavesRes, perfsRes, trainingsRes, claimsRes, feedbacksRes, balanceRes, formsRes] = results;
      setLeaves(leavesRes.status === 'fulfilled' ? leavesRes.value.data : []);
      setStudentLeaves(studentLeavesRes.status === 'fulfilled' ? studentLeavesRes.value.data : []);
      setApprovedLeaves(approvedLeavesRes.status === 'fulfilled' ? approvedLeavesRes.value.data : []);
      setPerformances(perfsRes.status === 'fulfilled' ? perfsRes.value.data : []);
      setTrainings(trainingsRes.status === 'fulfilled' ? trainingsRes.value.data : []);
      setClaims(claimsRes.status === 'fulfilled' ? claimsRes.value.data : []);
      setFeedbacks(feedbacksRes.status === 'fulfilled' ? feedbacksRes.value.data : []);
      setLeaveBalance(balanceRes.status === 'fulfilled' ? balanceRes.value.data : null);
      setForms(formsRes.status === 'fulfilled' ? (formsRes.value.data || []) : []);
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        console.error('Some dashboard data failed to load', failed);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchAttendanceRecords = async (year, month) => {
    try {
      const { data } = await axios.get(`${API}/api/attendance/records?year=${year}&month=${month}`, { withCredentials: true });
      setAttendanceRecords(data || []);
    } catch (err) {
      setAttendanceRecords([]);
    }
  };

  useEffect(() => {
    fetchAttendanceRecords(attendanceYearMonth.year, attendanceYearMonth.month);
  }, [attendanceYearMonth]);

  const getPosition = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('Geolocation not supported'));
    navigator.geolocation.getCurrentPosition((pos) => resolve(pos.coords), (err) => reject(err), { enableHighAccuracy: true, maximumAge: 0 });
  });

  const refreshToday = async () => {
    try {
      const { data } = await axios.get(`${API}/api/attendance/today`, { withCredentials: true });
      setAttendanceToday(data || null);
    } catch (err) {
      // ignore
    }
  };

  // Fetch today's record on mount so the UI reflects reality after page refresh
  useEffect(() => {
    refreshToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckIn = async () => {
    try {
      const coords = await getPosition();
      const payload = { lat: coords.latitude, lng: coords.longitude };
      const { data } = await axios.post(`${API}/api/attendance/checkin`, payload, { withCredentials: true });
      toast.success('Checked in');
      setAttendanceToday(data);
      refreshToday();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Check-in failed');
    }
  };

  const handleCheckOut = async () => {
    try {
      const coords = await getPosition();
      const payload = { lat: coords.latitude, lng: coords.longitude };
      const { data } = await axios.post(`${API}/api/attendance/checkout`, payload, { withCredentials: true });
      toast.success('Checked out');
      setAttendanceToday(data);
      refreshToday();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Check-out failed');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setPasswordSaving(true);
    try {
      await axios.post(
        `${API}/api/auth/change-password`,
        { current_password: passwordForm.current_password, new_password: passwordForm.new_password },
        { withCredentials: true }
      );
      toast.success('Password changed successfully');
      setChangePasswordOpen(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to change password';
      toast.error(typeof msg === 'string' ? msg : 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleFormProgressUpdate = async (formId, status) => {
    try {
      await axios.post(`${API}/api/forms/${formId}/progress`, { status }, { withCredentials: true });
      toast.success(status === 'submitted' ? 'Form submitted for review' : 'Form progress updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update form progress');
    }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/leaves`, leaveForm, { withCredentials: true });
      toast.success('Leave application submitted successfully');
      setLeaveForm({ leave_type: 'sick_leave', start_date: '', end_date: '', reason: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to submit leave application');
    }
  };

  const handleCancelLeave = async (leaveId, leaveName) => {
    if (!window.confirm(`Are you sure you want to cancel this ${leaveName} leave? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/api/leaves/${leaveId}`, { withCredentials: true });
      toast.success('Leave cancelled successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel leave');
    }
  };

  const handleTrainingSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/trainings`, trainingForm, { withCredentials: true });
      toast.success('Training record added successfully');
      setTrainingForm({ title: '', description: '', date: '', duration: '', certificate_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add training record');
    }
  };

  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/claims`, { ...claimForm, amount: parseFloat(claimForm.amount) }, { withCredentials: true });
      toast.success('Claim submitted successfully');
      setClaimForm({ claim_type: 'travel', transaction_date: '', amount: '', description: '', receipt_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to submit claim');
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/feedback`, feedbackForm, { withCredentials: true });
      toast.success('Feedback submitted successfully');
      setFeedbackForm({ subject: '', message: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B]',
      approved: 'bg-[#D1FAE5] text-[#10B981] border-[#10B981]',
      rejected: 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444]',
    };
    return <Badge className={`${variants[status]} border`} data-testid={`status-badge-${status}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</Badge>;
  };

  const leaveStats = {
    total: leaves.length,
    pending: leaves.filter((l) => l.status === 'pending').length,
    approved: leaves.filter((l) => l.status === 'approved').length,
  };

  const latestPerformance = performances[performances.length - 1];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="teacher-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_teach-track-48/artifacts/r4w4bz0k_IMG_20260701_210749.png" 
                alt="SSTC Logo" 
                className="w-full h-full object-contain"
                data-testid="sstc-logo-header"
              />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                SSTC e-Management System
              </h1>
              <p className="text-xs text-slate-500">Teacher Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <ProfileAvatar size="md" testId="header-profile-avatar" />
            <div className="text-right">
              <p className="text-sm font-medium text-slate-900" data-testid="user-name">{user?.name}</p>
              <p className="text-xs text-slate-500" data-testid="user-department">{user?.department}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setChangePasswordOpen(true)}
              data-testid="change-password-button"
              title="Change Password"
            >
              <KeyRound className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
          <Card className="bg-white border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid="leave-stats-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Annual leave</span>
              </div>
              <p className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="leave-balance-remaining">
                {leaveBalance ? `${leaveBalance.annual_leave_remaining}` : leaveStats.total}
                <span className="text-base text-slate-500 font-normal">
                  {leaveBalance ? ` / ${leaveBalance.annual_leave_allowance} days` : ''}
                </span>
              </p>
              <p className="text-sm text-slate-600 mt-1">
                {leaveBalance
                  ? `${leaveBalance.annual_leave_used} used, ${leaves.filter(l => l.status === 'pending' && (l.leave_type === 'annual_leave_1_day' || l.leave_type === 'annual_leave_half_day')).length} pending annual`
                  : `${leaveStats.pending} pending, ${leaveStats.approved} approved`}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid="sick-leave-stats-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Sick Leave</span>
              </div>
              <p className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="sick-leave-balance-remaining">
                {leaveBalance ? `${leaveBalance.sick_leave_remaining}` : '0'}
                <span className="text-base text-slate-500 font-normal">
                  {leaveBalance ? ` / ${leaveBalance.sick_leave_allowance} days` : ''}
                </span>
              </p>
              <p className="text-sm text-slate-600 mt-1">
                {leaveBalance ? `${leaveBalance.sick_leave_used} used` : 'No data available'}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid="training-stats-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <BookOpen className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Trainings</span>
              </div>
              <p className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{trainings.length}</p>
              <p className="text-sm text-slate-600 mt-1">Completed trainings</p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid="attendance-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Attendance</span>
              </div>
              <p className="text-lg font-semibold tracking-tight text-slate-900 mb-2" data-testid="attendance-today-label">{new Date().toLocaleDateString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })}</p>
              <div className="flex items-center gap-2">
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                  onClick={handleCheckIn}
                  disabled={!!(attendanceToday && attendanceToday.time_in)}
                  data-testid="checkin-button"
                >
                  Check In
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                  onClick={handleCheckOut}
                  disabled={!attendanceToday || !attendanceToday.time_in || !!attendanceToday.time_out}
                  data-testid="checkout-button"
                >
                  Check Out
                </Button>
              </div>
              {attendanceToday && (
                <div className="mt-3 text-sm text-slate-600">
                  <div>In: {attendanceToday.time_in ? new Date(attendanceToday.time_in).toLocaleTimeString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' }) : '—'}</div>
                  <div>Out: {attendanceToday.time_out ? new Date(attendanceToday.time_out).toLocaleTimeString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' }) : '—'}</div>
                  <div>Duration: {attendanceToday.duration_seconds ? `${Math.floor(attendanceToday.duration_seconds/3600)}h ${Math.floor((attendanceToday.duration_seconds%3600)/60)}m` : '—'}</div>
                  <div>Late: {attendanceToday.late_seconds ? `${Math.floor(attendanceToday.late_seconds/3600)}h ${Math.floor((attendanceToday.late_seconds%3600)/60)}m ${attendanceToday.late_seconds%60}s` : (attendanceToday.is_late ? '0s' : '—')}</div>
                  <div>Overtime: {attendanceToday.overtime_seconds ? `${Math.floor(attendanceToday.overtime_seconds/3600)}h ${Math.floor((attendanceToday.overtime_seconds%3600)/60)}m ${attendanceToday.overtime_seconds%60}s` : (attendanceToday.is_overtime ? '0s' : '—')}</div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid="claims-stats-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Claims</span>
              </div>
              <p className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{claims.length}</p>
              <p className="text-sm text-slate-600 mt-1">{claims.filter((c) => c.status === 'pending').length} pending</p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200" data-testid="performance-stats-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Rating</span>
              </div>
              <p className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {latestPerformance ? `${latestPerformance.rating}/5` : 'N/A'}
              </p>
              <p className="text-sm text-slate-600 mt-1">Latest performance</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="news" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 flex-wrap h-auto" data-testid="teacher-tabs">
            <TabsTrigger value="news" data-testid="tab-news">News & Announcements</TabsTrigger>
            <TabsTrigger value="kudos" data-testid="tab-kudos">Kudos</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="students" data-testid="tab-students">My Students</TabsTrigger>
            <TabsTrigger value="leaves" data-testid="tab-leaves">Leaves</TabsTrigger>
            <TabsTrigger value="student-leaves" data-testid="tab-student-leaves">Student Leaves</TabsTrigger>
            <TabsTrigger value="team-leaves" data-testid="tab-team-leaves">Team Leaves</TabsTrigger>
            <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
            <TabsTrigger value="trainings" data-testid="tab-trainings">Trainings</TabsTrigger>
            <TabsTrigger value="claims" data-testid="tab-claims">Claims</TabsTrigger>
            <TabsTrigger value="payslips" data-testid="tab-payslips">Payslips</TabsTrigger>
            <TabsTrigger value="purchases" data-testid="tab-purchases">Purchase List</TabsTrigger>
            <TabsTrigger value="attendance_history" data-testid="tab-attendance">Attendance</TabsTrigger>
            <TabsTrigger value="form" data-testid="tab-form">Form</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <ProfileForm />
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <TeacherStudentsSection />
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news">
            <div className="space-y-6">
              <NewsSection isAdmin={false} />
              <BirthdaySection />
              <CalendarEventsSection isAdmin={false} />
            </div>
          </TabsContent>

          {/* Kudos Tab */}
          <TabsContent value="kudos">
            <div className="space-y-6">
              <KudosSection isAdmin={false} />
            </div>
          </TabsContent>

          {/* Form Tab */}
          <TabsContent value="form">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Form</CardTitle>
                <CardDescription>Open admin-published forms and submit completion progress.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Form</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last Updated</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="forms-table">
                      {forms.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-slate-500">No forms available yet</TableCell>
                        </TableRow>
                      ) : (
                        forms.map((form, idx) => (
                          <TableRow key={form.id} data-testid={`form-row-${idx}`}>
                            <TableCell className="font-medium">
                              <a href={form.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                {form.title}
                              </a>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">{form.description || 'No description provided'}</TableCell>
                            <TableCell>
                              <Badge className={`border ${form.progress_status === 'submitted' ? 'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B]' : form.progress_status === 'in_progress' ? 'bg-blue-100 text-blue-700 border-blue-300 border' : form.progress_status === 'approved' ? 'bg-[#D1FAE5] text-[#10B981] border-[#10B981]' : form.progress_status === 'rejected' ? 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444]' : 'bg-slate-100 text-slate-700 border-slate-300 border'}`}>
                                {form.progress_status?.replace('_', ' ').toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">{form.progress_updated_at ? new Date(form.progress_updated_at).toLocaleString() : '—'}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-2">
                                <Button size="sm" className="bg-slate-100 text-slate-700 hover:bg-slate-200" disabled={!form.url} onClick={() => form.url && window.open(form.url, '_blank', 'noopener,noreferrer')}>
                                  Open
                                </Button>
                                {form.progress_status === 'not_started' && (
                                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => handleFormProgressUpdate(form.id, 'in_progress')}>
                                    Start
                                  </Button>
                                )}
                                {form.progress_status === 'in_progress' && (
                                  <Button size="sm" className="bg-[#F59E0B] hover:bg-[#D97706] text-white" onClick={() => handleFormProgressUpdate(form.id, 'submitted')}>
                                    Submit for review
                                  </Button>
                                )}
                                {form.progress_status === 'rejected' && (
                                  <Button size="sm" className="bg-[#EF4444] hover:bg-[#DC2626] text-white" onClick={() => handleFormProgressUpdate(form.id, 'submitted')}>
                                    Resubmit
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance History Tab */}
          <TabsContent value="attendance_history">
            <div className="space-y-6">
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Attendance History</CardTitle>
                  <CardDescription>View your attendance records for a selected month</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 mb-4">
                    <label className="text-sm text-slate-600">Month</label>
                    <input type="month" value={`${attendanceYearMonth.year}-${String(attendanceYearMonth.month).padStart(2,'0')}`} onChange={(e) => {
                      const [y, m] = e.target.value.split('-');
                      setAttendanceYearMonth({ year: parseInt(y,10), month: parseInt(m,10) });
                    }} />
                    <Button onClick={() => fetchAttendanceRecords(attendanceYearMonth.year, attendanceYearMonth.month)} className="bg-blue-600 hover:bg-blue-700 text-white">Refresh</Button>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Time In</TableHead>
                          <TableHead>Time Out</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Late</TableHead>
                          <TableHead>Overtime</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {attendanceRecords.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-slate-500">No attendance records for this month</TableCell>
                          </TableRow>
                        ) : (
                          attendanceRecords.map((rec, idx) => (
                            <TableRow key={rec.id || idx}>
                              <TableCell>{rec.date}</TableCell>
                              <TableCell>{rec.time_in ? new Date(rec.time_in).toLocaleTimeString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' }) : '—'}</TableCell>
                              <TableCell>{rec.time_out ? new Date(rec.time_out).toLocaleTimeString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' }) : '—'}</TableCell>
                              <TableCell>{rec.duration_seconds ? `${Math.floor(rec.duration_seconds/3600)}h ${Math.floor((rec.duration_seconds%3600)/60)}m` : '—'}</TableCell>
                              <TableCell>{rec.is_late ? 'Yes' : 'No'}</TableCell>
                              <TableCell>{rec.is_overtime ? 'Yes' : 'No'}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Leave Management Tab */}
          <TabsContent value="leaves" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Leave History</CardTitle>
                  <CardDescription>View your leave applications and their status</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Dates</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody data-testid="leave-history-table">
                        {leaves.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-slate-500">No leave applications yet</TableCell>
                          </TableRow>
                        ) : (
                          leaves.map((leave, idx) => (
                            <TableRow key={leave.id} data-testid={`leave-row-${idx}`}>
                              <TableCell className="font-medium">{leave.leave_type.replace('_', ' ').toUpperCase()}</TableCell>
                              <TableCell className="text-sm text-slate-600">{leave.start_date} to {leave.end_date}</TableCell>
                              <TableCell className="text-sm text-slate-600">{leave.reason}</TableCell>
                              <TableCell>{getStatusBadge(leave.status)}</TableCell>
                              <TableCell>
                                {(leave.status === 'pending' || leave.status === 'approved') && (
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                    onClick={() => handleCancelLeave(leave.id, leave.leave_type.replace('_', ' ').toLowerCase())}
                                    data-testid={`cancel-leave-${idx}`}
                                  >
                                    Cancel
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Apply for Leave</CardTitle>
                  <CardDescription>Submit a new leave application</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLeaveSubmit} className="space-y-4" data-testid="leave-application-form">
                    <div>
                      <Label htmlFor="leave_type">Leave Type</Label>
                      <Select value={leaveForm.leave_type} onValueChange={(val) => setLeaveForm({ ...leaveForm, leave_type: val })}>
                        <SelectTrigger data-testid="leave-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sick_leave">Sick Leave</SelectItem>
                          <SelectItem value="annual_leave_1_day">Annual leave (1 Day)</SelectItem>
                          <SelectItem value="annual_leave_half_day">Annual leave (Half Day)</SelectItem>
                          <SelectItem value="emergency_leave">Emergency Leave</SelectItem>
                          <SelectItem value="convocation_leave">Convocation Leave</SelectItem>
                          <SelectItem value="unpaid_leave">Unpaid Leave</SelectItem>
                          <SelectItem value="bereavement_leave">Bereavement Leave</SelectItem>
                          <SelectItem value="maternity_leave">Maternity Leave</SelectItem>
                          <SelectItem value="paternity_leave">Paternity Leave</SelectItem>
                        </SelectContent>
                      </Select>
                      {leaveForm.leave_type === 'sick_leave' && (
                        <p className="mt-2 text-sm text-amber-700 bg-amber-100 p-3 rounded border border-amber-200">
                          Please submit your MC to Pn Sarah / En Zul through whatsapp
                        </p>
                      )}
                      {leaveForm.leave_type === 'convocation_leave' && (
                        <p className="mt-2 text-sm text-amber-700 bg-amber-100 p-3 rounded border border-amber-200">
                          Please send your convo invitation letter (for prove record) to Pn Sarah / En Zul through whatsapp
                        </p>
                      )}
                      {leaveForm.leave_type === 'bereavement_leave' && (
                        <p className="mt-2 text-sm text-amber-700 bg-amber-100 p-3 rounded border border-amber-200">
                          Please send death certificate or related document to Pn Sarah / En Zul through whatsapp
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="start_date">Start Date</Label>
                      <Input
                        type="date"
                        value={leaveForm.start_date}
                        onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })}
                        required
                        data-testid="leave-start-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_date">End Date</Label>
                      <Input
                        type="date"
                        value={leaveForm.end_date}
                        onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                        required
                        data-testid="leave-end-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="reason">Reason</Label>
                      <Textarea
                        value={leaveForm.reason}
                        onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                        required
                        placeholder="Explain the reason for your leave..."
                        data-testid="leave-reason-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-leave-button">
                      Submit Application
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Student Leaves Tab */}
          <TabsContent value="student-leaves" className="space-y-4">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Student Leaves</CardTitle>
                    <CardDescription>View and manage student leave requests</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {studentLeaves.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <p>No student leave requests yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Student</TableHead>
                          <TableHead>Dates</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Submitted By</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {studentLeaves.map((leave, idx) => (
                          <TableRow key={leave.id} data-testid={`student-leave-row-${idx}`}>
                            <TableCell className="font-medium">{leave.student_name}</TableCell>
                            <TableCell className="text-sm text-slate-600">{leave.start_date} to {leave.end_date}</TableCell>
                            <TableCell className="text-sm text-slate-600">{leave.reason}</TableCell>
                            <TableCell className="text-sm text-slate-600">{leave.parent_name}</TableCell>
                            <TableCell>{leave.status}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Leaves Tab */}
          <TabsContent value="team-leaves">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Team Leaves - {new Date().getFullYear()}</CardTitle>
                <CardDescription>View all approved leaves for the current year</CardDescription>
              </CardHeader>
              <CardContent>
                {approvedLeaves.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                    <p>No approved leaves this year</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Teacher</TableHead>
                          <TableHead>Leave Type</TableHead>
                          <TableHead>From</TableHead>
                          <TableHead>To</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {approvedLeaves.map((leave, idx) => (
                          <TableRow key={leave.id} data-testid={`team-leave-row-${idx}`}>
                            <TableCell className="font-medium">{leave.user_name}</TableCell>
                            <TableCell className="text-sm">{leave.leave_type.replace('_', ' ').toUpperCase()}</TableCell>
                            <TableCell className="text-sm text-slate-600">{leave.start_date}</TableCell>
                            <TableCell className="text-sm text-slate-600">{leave.end_date}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6">
            <GoalsSection isAdmin={false} />
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Performance Reviews from Admin</CardTitle>
                <CardDescription>Reviews and feedback from your manager</CardDescription>
              </CardHeader>
              <CardContent>
                {performances.length === 0 ? (
                  <div className="text-center py-12 text-slate-500" data-testid="no-performance-message">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                    <p>No performance reviews yet</p>
                  </div>
                ) : (
                  <div className="space-y-6" data-testid="performance-list">
                    {performances.map((perf, idx) => (
                      <div key={perf.id} className="p-6 border border-slate-200 rounded-lg" data-testid={`performance-item-${idx}`}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-slate-900">{perf.review_period}</h3>
                          <Badge className="bg-blue-100 text-blue-700 border-blue-300 border" data-testid="performance-rating">Rating: {perf.rating}/5</Badge>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Goals</p>
                            <p className="text-sm text-slate-700">{perf.goals}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Achievements</p>
                            <p className="text-sm text-slate-700">{perf.achievements}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Areas of Improvement</p>
                            <p className="text-sm text-slate-700">{perf.areas_of_improvement}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trainings Tab */}
          <TabsContent value="trainings">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Training Records</CardTitle>
                  <CardDescription>List of trainings you've attended</CardDescription>
                </CardHeader>
                <CardContent>
                  {trainings.length === 0 ? (
                    <div className="text-center py-12 text-slate-500" data-testid="no-trainings-message">
                      <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                      <p>No training records yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4" data-testid="training-list">
                      {trainings.map((training, idx) => (
                        <div key={training.id} className="p-4 border border-slate-200 rounded-lg" data-testid={`training-item-${idx}`}>
                          <h4 className="font-semibold text-slate-900 mb-1">{training.title}</h4>
                          <p className="text-sm text-slate-600 mb-2">{training.description}</p>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span>Date: {training.date}</span>
                            <span>Duration: {training.duration}</span>
                            {training.certificate_url && (
                              <a
                                href={`${API}/api/files/${training.certificate_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-700 underline"
                                data-testid={`training-certificate-link-${idx}`}
                              >
                                View Certificate
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Add Training</CardTitle>
                  <CardDescription>Record a training you attended</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleTrainingSubmit} className="space-y-4" data-testid="add-training-form">
                    <div>
                      <Label htmlFor="title">Training Title</Label>
                      <Input
                        value={trainingForm.title}
                        onChange={(e) => setTrainingForm({ ...trainingForm, title: e.target.value })}
                        required
                        placeholder="e.g., Advanced Teaching Methods"
                        data-testid="training-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        value={trainingForm.description}
                        onChange={(e) => setTrainingForm({ ...trainingForm, description: e.target.value })}
                        required
                        placeholder="Brief description..."
                        data-testid="training-description-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="date">Date</Label>
                      <Input
                        type="date"
                        value={trainingForm.date}
                        onChange={(e) => setTrainingForm({ ...trainingForm, date: e.target.value })}
                        required
                        data-testid="training-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="duration">Duration</Label>
                      <Input
                        value={trainingForm.duration}
                        onChange={(e) => setTrainingForm({ ...trainingForm, duration: e.target.value })}
                        required
                        placeholder="e.g., 3 hours"
                        data-testid="training-duration-input"
                      />
                    </div>
                    <div>
                      <Label>Certificate (optional)</Label>
                      <FileUpload
                        testIdPrefix="training-certificate"
                        currentFileId={trainingForm.certificate_url}
                        onFileUploaded={(f) => setTrainingForm({ ...trainingForm, certificate_url: f ? f.id : '' })}
                      />
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-training-button">
                      Add Training
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Claims Tab */}
          <TabsContent value="claims">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2 bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Claims History</CardTitle>
                  <CardDescription>View your submitted claims</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody data-testid="claims-table">
                        {claims.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center text-slate-500">No claims submitted yet</TableCell>
                          </TableRow>
                        ) : (
                          claims.map((claim, idx) => (
                            <TableRow key={claim.id} data-testid={`claim-row-${idx}`}>
                              <TableCell className="font-medium">{claim.claim_type.toUpperCase()}</TableCell>
                              <TableCell className="text-right">${claim.amount.toFixed(2)}</TableCell>
                              <TableCell className="text-sm text-slate-600">{claim.description}</TableCell>
                              <TableCell>{getStatusBadge(claim.status)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Submit Claim</CardTitle>
                  <CardDescription>Request reimbursement</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleClaimSubmit} className="space-y-4" data-testid="submit-claim-form">
                    <div>
                      <Label htmlFor="claim_type">Claim Type</Label>
                      <Select value={claimForm.claim_type} onValueChange={(val) => setClaimForm({ ...claimForm, claim_type: val })}>
                        <SelectTrigger data-testid="claim-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="travel">Travel</SelectItem>
                          <SelectItem value="accommodation">Accommodation</SelectItem>
                          <SelectItem value="meals">Meals</SelectItem>
                          <SelectItem value="health_screening">Health Screening</SelectItem>
                          <SelectItem value="medical_treatment">Medical Treatment</SelectItem>
                          <SelectItem value="buddy_lunch">Buddy Lunch</SelectItem>
                          <SelectItem value="team_event">Team Event</SelectItem>
                          <SelectItem value="supplies">Supplies</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="transaction_date">Transaction Date</Label>
                      <Input
                        type="date"
                        value={claimForm.transaction_date}
                        onChange={(e) => setClaimForm({ ...claimForm, transaction_date: e.target.value })}
                        required
                        data-testid="claim-transaction-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="amount">Amount ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={claimForm.amount}
                        onChange={(e) => setClaimForm({ ...claimForm, amount: e.target.value })}
                        required
                        placeholder="0.00"
                        data-testid="claim-amount-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        value={claimForm.description}
                        onChange={(e) => setClaimForm({ ...claimForm, description: e.target.value })}
                        required
                        placeholder="Describe your claim..."
                        data-testid="claim-description-input"
                      />
                    </div>
                    <div>
                      <Label>Receipt (optional)</Label>
                      <FileUpload
                        testIdPrefix="claim-receipt"
                        currentFileId={claimForm.receipt_url}
                        onFileUploaded={(f) => setClaimForm({ ...claimForm, receipt_url: f ? f.id : '' })}
                      />
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-claim-button">
                      Submit Claim
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Payslips Tab */}
          <TabsContent value="payslips">
            <TeacherPayslipsSection />
          </TabsContent>

          {/* Purchase List Tab */}
          <TabsContent value="purchases">
            <PurchaseItemsSection />
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback">
            <FeedbackSection />
          </TabsContent>
        </Tabs>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent className="max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one (min 6 characters)</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
            <div>
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                required
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button type="button" variant="outline" onClick={() => setChangePasswordOpen(false)} data-testid="cancel-password-button">
                Cancel
              </Button>
              <Button type="submit" disabled={passwordSaving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-password-button">
                {passwordSaving ? 'Saving...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherDashboard;
